import { memo, useEffect, useState } from 'react'

import { Modal } from '@inno-ui/modal'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useUserContext } from 'shared/hooks/useUserContext'
import { isProd } from 'shared/utils/isProd'

import {
  APPOINTLY_NON_PROD_SLUG,
  APPOINTLY_PROD_SLUG
} from './TrialExpiryModal.constants'
import styles from './TrialExpiryModal.module.scss'
import { TrialExpiryButton } from './components'

export const TrialExpiryModal = memo(() => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'trial_expiry_modal'
  })

  const [isOpen, setIsOpen] = useState(false)

  const handleOpenConsultation = () => {
    const baseUrl = process.env.REACT_APP_PUBLIC_CALENDAR_APPOINTLY_BASE_URL
    if (!baseUrl) return

    const language = i18n.language?.split('-')[0] ?? 'en'

    const path = isProd() ? APPOINTLY_PROD_SLUG : APPOINTLY_NON_PROD_SLUG

    const url = `${baseUrl}/${path}?l=${encodeURIComponent(language)}`

    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const { user } = useUserContext()
  const { trial_expiry, is_trial_account } = user ?? {}

  useEffect(() => {
    if (!trial_expiry || !is_trial_account) return

    const MAX_TIMEOUT_MS = 2 ** 31 - 1
    let timeoutId: number | undefined

    const schedule = () => {
      const targetMs = trial_expiry * 1000
      const timeLeftMs = targetMs - Date.now()

      if (timeLeftMs <= 0) {
        setIsOpen(true)
        return
      }

      const delay = Math.min(timeLeftMs, MAX_TIMEOUT_MS)
      timeoutId = window.setTimeout(() => {
        schedule()
      }, delay)
    }

    schedule()

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }, [trial_expiry, is_trial_account])

  if (typeof document === 'undefined') return null

  return createPortal(
    <Modal
      isOpen={isOpen}
      maxWidth={1182}
      maxHeight={546}
      zIndex={1004}
      closeOnClickOutside={false}
      layers={[
        {
          showHeader: false,
          bodyPaddings: false,
          body: (
            <div className={styles.modalContent} data-modal-content>
              <div className={styles.leftSection}>
                <div className={styles.infoSection}>
                  <h1 className={styles.title}>{t('title')}</h1>
                  <p className={styles.description}>{t('description')}</p>
                </div>
                <TrialExpiryButton
                  label={t('button_label')}
                  onClick={handleOpenConsultation}
                />
              </div>
              <div className={styles.rightSection}>
                <div className={styles.imageWrapper}>
                  <img
                    src={'/trial-expiry-modal/' + 'head-of-sales.webp'}
                    alt={t('sales_person_name')}
                    className={styles.personImage}
                  />
                  <div
                    className={styles.patternOverlay}
                    style={{
                      backgroundImage:
                        'url("/trial-expiry-modal/sales-modal-clusterix-logo.svg")'
                    }}
                  />
                </div>
              </div>
              <div className={styles.personInfo}>
                <h2 className={styles.personName}>{t('sales_person_name')}</h2>
                <p className={styles.personTitle}>{t('sales_person_title')}</p>
              </div>
            </div>
          )
        }
      ]}
    />,
    document.body
  )
})
